/* @flow */
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import cx from 'classnames';
import idx from 'idx';
import * as React from 'react';
import PropTypes from 'prop-types';

import { saveStartUrl } from 'admin/reducers/adminUpgradePage';
import AdminUpgradeHelpModal from 'admin/components/AdminUpgradeHelpModal';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Container from 'components/Container';
import EventTracking from 'lib/EventTracking';
import LinkConstants from 'constants/LinkConstants';
import ModalTrigger from 'components/ModalTrigger';
import Notice from 'components/Notice';
import ORGANIZATION_ADMIN_UPSELL_QUERY from 'organizations/queries/OrganizationAdminUpsellQuery.gql';
import OrganizationUpsellHeader from './OrganizationUpsellHeader';
import Row from 'components/Row';
import Text from 'components/Text';

import type { OrganizationAdminUpsell as OrganizationAdminUpsellQuery } from 'organizations/queries/__generated__/OrganizationAdminUpsell';

import './styles/UnpaidOrganizationsNull.scss';

type Props = {
  className?: string,
  isPaid: boolean,
  isPendingAdmin: boolean,
  organizationUuid: string,
  saveStartUrl: (url: ?string) => *,
  data: ReactApolloData<OrganizationAdminUpsellQuery>,
};

class OrganizationAdminUpsell extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  componentDidMount() {
    EventTracking.trackKey('admin_first_onboarding.UnpaidOrganizationsNull.admin.view');
  }

  saveStartUrl = () => {
    this.props.saveStartUrl(this.context.router.getCurrentLocation().pathname);
  };

  renderHelpModal = () => {
    const utmParams = {
      utm_source: LinkConstants.UTM_SOURCE_ADMIN,
      utm_medium: LinkConstants.UTM_MEDIUM_PRODUCT,
      utm_campaign: LinkConstants.UTM_CAMPAIGN_UNPAID_ORG,
    };
    return (
      <AdminUpgradeHelpModal
        organizationUuid={this.props.organizationUuid}
        source="admin_onboarding"
        utmParams={utmParams}
      />
    );
  };

  render() {
    const { className, organizationUuid, isPendingAdmin, isPaid, data } = this.props;
    const { loading } = data;
    // Using billing email as a proxy for whether they have completed self-serve since it appears on the last page.
    const previouslyRequestedInvoice = idx(
      data,
      _ => _.organizations[0].quoteRequestedByUser.billingEmail
    );
    const hasCreditCard = idx(data, _ => _.organizations[0].billingCreditCard);
    const hasCompletedSelfServe = previouslyRequestedInvoice || hasCreditCard;

    return (
      <div className={cx('OrganizationAdminUpsell UnpaidOrganizationsNull', className)}>
        <div className="UnpaidOrganizationsNull--content">
          <Row size="xxxlarge">
            <Text color="pitch">
              <OrganizationUpsellHeader userRole="admin" />
            </Text>
          </Row>
          <Row size="xxxlarge">
            {isPaid && isPendingAdmin && (
              <Row size="small">
                <Notice color="flame">
                  Your organization has not yet been verified - check back soon.
                </Notice>
              </Row>
            )}
            <Container size="xxsmall">
              <Buttons axis="y">
                {!isPaid && !hasCompletedSelfServe && !loading && (
                  <Button
                    data-test-selector="buy-now-button"
                    onClick={this.saveStartUrl}
                    to={`/admin_upgrade/${organizationUuid}?utm_source=${LinkConstants.UTM_SOURCE_ADMIN}&utm_medium=${LinkConstants.UTM_MEDIUM_PRODUCT}&utm_campaign=${LinkConstants.UTM_CAMPAIGN_UNPAID_ORG}`}
                    eventKey="admin_first_onboarding.UnpaidOrganizationsNull.admin.buy_now.click">
                    Buy now
                  </Button>
                )}
                {!isPaid && !hasCompletedSelfServe && !loading && (
                  <ModalTrigger modal={this.renderHelpModal()}>
                    <Button
                      color="brand"
                      level="text"
                      weight="semibold"
                      onClick={this.saveStartUrl}
                      eventKey="admin_first_onboarding.UnpaidOrganizationsNull.admin.contact_us.click">
                      Contact us
                    </Button>
                  </ModalTrigger>
                )}
              </Buttons>
            </Container>
          </Row>
          {!isPaid && hasCompletedSelfServe && !loading && (
            <Container size="small">
              <Text color="thunder">
                Our School Success Team will begin turning on features for your school within two
                business days. You’ll also receive a series of emails with next steps for rolling
                out Remind at your school.
              </Text>
            </Container>
          )}
        </div>
      </div>
    );
  }
}

const withOrganization = graphql(ORGANIZATION_ADMIN_UPSELL_QUERY, {
  options: ({ organizationUuid }) => ({
    variables: {
      organizationUuid: [organizationUuid],
    },
  }),
});

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ saveStartUrl }, dispatch);
}

export { OrganizationAdminUpsell };
export default connect(undefined, mapDispatchToProps)(withOrganization(OrganizationAdminUpsell));
